import React, { useState, useEffect, useRef } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useConfiguration } from '../../context/configurationContext';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import {
  Page,
  LayoutSingleColumn,
  NamedLink,
  Avatar,
} from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import { apiBaseUrl } from '../../util/api';

import css from './AllSellersPage.module.css';
import sevaSethuLogo from '../../assets/seva-sethu/seva-sethu-logo.png';

// Seller categories - matches Sharetribe Console categories
const SELLER_CATEGORIES = [
  { key: 'food-prasadam', label: 'Food & Prasadam', color: '#F44336', description: 'Fresh meals, catering, packaged foods, and sattvic prasadam.' },
  { key: 'music-instruments', label: 'Music & Instruments', color: '#009688', description: 'Mridanga, harmonium, kartals, and devotional music supplies.' },
  { key: 'books-media', label: 'Books & Media', color: '#2196F3', description: 'Scriptures, philosophy books, audio/video, and devotional literature.' },
  { key: 'clothing-accessories', label: 'Clothing & Accessories', color: '#673AB7', description: 'Dhotis, kurtas, sarees, tulsi malas, and devotional jewelry.' },
  { key: 'health-wellness', label: 'Health & Wellness', color: '#8BC34A', description: 'Ayurvedic products, herbal supplements, and yoga services.' },
  { key: 'home-puja', label: 'Home & Puja Supplies', color: '#795548', description: 'Deities, puja items, incense, altar supplies, and sacred plants.' },
  { key: 'art-decor', label: 'Art & Decor', color: '#9C27B0', description: 'Paintings, sculptures, handicrafts, and devotional gift items.' },
  { key: 'spiritual-services', label: 'Spiritual Services', color: '#FF9800', description: 'Priest services, astrology, Vastu, and ceremony arrangements.' },
  { key: 'education', label: 'Education & Classes', color: '#E07912', description: 'Music lessons, scripture study, language classes, and mentoring.' },
  { key: 'professional-services', label: 'Professional Services', color: '#607D8B', description: 'Finance, real estate, tax consulting, and legal services.' },
];

// Seller Card Component
const SellerCard = ({ seller, listingCount }) => {
  const profile = seller.attributes?.profile || {};
  const displayName = profile.displayName || 'Seller';
  const bio = profile.bio || profile.publicData?.businessDescription || '';
  const abbreviatedBio = bio ? (bio.length > 100 ? bio.substring(0, 100) + '...' : bio) : 'Devotional products and services';

  return (
    <div className={css.sellerCard}>
      <div className={css.sellerAvatar}>
        <Avatar user={seller} className={css.avatar} disableProfileLink />
      </div>
      <h3 className={css.sellerName}>{displayName}</h3>
      <p className={css.sellerBio}>{abbreviatedBio}</p>
      <p className={css.productCount}>{listingCount} products</p>
      <NamedLink
        name="ProfilePage"
        params={{ id: seller.id.uuid }}
        className={css.viewProductsBtn}
      >
        View Products
      </NamedLink>
    </div>
  );
};

// Category Card (left side label)
const CategoryCard = ({ category, sellerCount, onClick }) => {
  return (
    <div className={css.categoryCard} style={{ backgroundColor: category.color }}>
      <h3 className={css.categoryCardTitle}>{category.label}</h3>
      <p className={css.categoryCardCount}>{sellerCount} vendors</p>
      <button className={css.viewAllBtn} onClick={onClick}>
        View All →
      </button>
    </div>
  );
};

// Horizontal Carousel Section
const CategoryCarouselSection = ({ category, sellers, listingCounts, onViewAll }) => {
  const scrollRef = useRef(null);

  if (!sellers || sellers.length === 0) return null;

  const scrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  return (
    <section className={css.carouselSection}>
      <div className={css.sectionHeader}>
        <h2 className={css.categoryTitle}>{category.label}</h2>
        <p className={css.categoryDescription}>{category.description}</p>
      </div>

      <div className={css.carouselContainer}>
        {/* Category Card on the left */}
        <CategoryCard
          category={category}
          sellerCount={sellers.length}
          onClick={onViewAll}
        />

        {/* Scrollable sellers */}
        <div className={css.carouselWrapper}>
          <button className={css.scrollBtn + ' ' + css.scrollLeft} onClick={scrollLeft}>
            ‹
          </button>
          <div className={css.carouselScroll} ref={scrollRef}>
            {sellers.map(seller => (
              <SellerCard
                key={seller.id.uuid}
                seller={seller}
                listingCount={listingCounts[seller.id.uuid] || 0}
              />
            ))}
          </div>
          <button className={css.scrollBtn + ' ' + css.scrollRight} onClick={scrollRight}>
            ›
          </button>
        </div>
      </div>
    </section>
  );
};

const AllSellersPageComponent = props => {
  const { scrollingDisabled } = props;
  const config = useConfiguration();

  const [sellers, setSellers] = useState([]);
  const [listingCounts, setListingCounts] = useState({});
  const [sellerCategories, setSellerCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    const fetchSellers = async () => {
      try {
        const baseUrl = apiBaseUrl();
        const response = await fetch(`${baseUrl}/api/sellers`);
        if (response.ok) {
          const data = await response.json();
          setSellers(data.sellers || []);
          setListingCounts(data.listingCounts || {});
          setSellerCategories(data.sellerCategories || {});
        } else {
          console.error('Failed to fetch sellers:', response.status, response.statusText);
        }
      } catch (error) {
        console.error('Error fetching sellers:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSellers();
  }, []);

  // Get the primary category for a seller based on their listings
  // Returns the category where they have the most listings
  const getSellerPrimaryCategory = (sellerId) => {
    const categories = sellerCategories[sellerId];
    if (!categories || Object.keys(categories).length === 0) {
      return null;
    }
    // Find the category with the most listings
    let primaryCategory = null;
    let maxCount = 0;
    Object.entries(categories).forEach(([category, count]) => {
      if (count > maxCount) {
        maxCount = count;
        primaryCategory = category;
      }
    });
    return primaryCategory;
  };

  // Get all categories a seller has listings in
  const getSellerAllCategories = (sellerId) => {
    const categories = sellerCategories[sellerId];
    if (!categories) return [];
    return Object.keys(categories);
  };

  // Search function - searches in name, bio, and business description
  const searchSeller = (seller, query) => {
    if (!query || query.trim() === '') return true;

    const profile = seller.attributes?.profile || {};
    const publicData = profile.publicData || {};

    // Collect all searchable fields
    const searchFields = [
      profile.displayName || '',
      profile.firstName || '',
      profile.lastName || '',
      profile.bio || '',
      publicData.businessDescription || '',
      publicData.businessName || '',
    ].join(' ').toLowerCase();

    // Clean up the query - handle partial matches
    const normalizedQuery = query.toLowerCase().trim();

    // First try: check if the entire query string is found (for partial word matches like "wisom" for "wisdom")
    if (searchFields.includes(normalizedQuery)) {
      return true;
    }

    // Second try: split query into words and check if all words are found
    const queryWords = normalizedQuery.split(/\s+/).filter(w => w.length > 0);
    return queryWords.every(word => searchFields.includes(word));
  };

  // Filter sellers by search query and category
  const filteredSellers = sellers.filter(seller => {
    const sellerId = seller.id?.uuid || seller.id;
    const sellerCats = getSellerAllCategories(sellerId);

    const matchesSearch = searchSeller(seller, searchQuery);
    // For category filtering, check if seller has ANY listings in that category
    const matchesCategory = activeCategory === 'all' || sellerCats.includes(activeCategory);

    return matchesSearch && matchesCategory;
  });

  // Group sellers by category based on their listings
  // A seller appears in a category if they have any listings in that category
  const sellersByCategory = SELLER_CATEGORIES.reduce((acc, category) => {
    acc[category.key] = filteredSellers.filter(seller => {
      const sellerId = seller.id?.uuid || seller.id;
      const sellerCats = getSellerAllCategories(sellerId);
      return sellerCats.includes(category.key);
    });
    return acc;
  }, {});

  // Count total sellers per category (for filter badges)
  // Count sellers who have ANY listings in that category
  const countByCategory = SELLER_CATEGORIES.reduce((acc, category) => {
    acc[category.key] = sellers.filter(seller => {
      const sellerId = seller.id?.uuid || seller.id;
      const sellerCats = sellerCategories[sellerId] || {};
      return sellerCats[category.key] > 0;
    }).length;
    return acc;
  }, {});

  const title = 'Our Community Sellers - Seva Sethu Marketplace';
  const description = 'Meet the trusted vendors from our temple community offering products and services with devotion and care.';

  return (
    <Page
      title={title}
      description={description}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn
        topbar={<TopbarContainer />}
        footer={<FooterContainer />}
      >
        <div className={css.pageWrapper}>
          {/* Hero Section */}
          <div className={css.heroSection}>
            <div className={css.logoIcon}>
              <img src={sevaSethuLogo} alt="Seva Sethu" className={css.logoImage} />
            </div>
            <p className={css.marketplaceLabel}>SEVA SETHU MARKETPLACE</p>
            <h1 className={css.pageTitle}>Our Community Sellers</h1>
            <p className={css.pageSubtitle}>
              Meet the trusted vendors from our temple community offering products and services with devotion and care.
            </p>
          </div>

          {/* Search and Filter Section */}
          <div className={css.filterSection}>
            <div className={css.searchContainer}>
              <span className={css.searchIcon}>🔍</span>
              <input
                type="text"
                placeholder="Search sellers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={css.searchInput}
              />
              {searchQuery && (
                <button
                  className={css.clearSearch}
                  onClick={() => setSearchQuery('')}
                >
                  ✕
                </button>
              )}
            </div>
            <div className={css.categoryFilters}>
              <button
                className={`${css.categoryBtn} ${activeCategory === 'all' ? css.active : ''}`}
                onClick={() => setActiveCategory('all')}
              >
                All
              </button>
              {SELLER_CATEGORIES.map(cat => (
                <button
                  key={cat.key}
                  className={`${css.categoryBtn} ${activeCategory === cat.key ? css.active : ''}`}
                  onClick={() => setActiveCategory(cat.key)}
                >
                  {cat.label}
                  {countByCategory[cat.key] > 0 && (
                    <span className={css.categoryCount}>{countByCategory[cat.key]}</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className={css.loadingContainer}>
              <div className={css.spinner}></div>
              <p>Loading sellers...</p>
            </div>
          )}

          {/* Search Results Info */}
          {!loading && searchQuery && (
            <div className={css.searchResults}>
              <p>Found {filteredSellers.length} seller{filteredSellers.length !== 1 ? 's' : ''} matching "{searchQuery}"</p>
            </div>
          )}

          {/* Category Carousels - always show carousels (with or without search) */}
          {!loading && activeCategory === 'all' && (
            <div className={css.categoriesContainer}>
              {SELLER_CATEGORIES.map(category => (
                <CategoryCarouselSection
                  key={category.key}
                  category={category}
                  sellers={sellersByCategory[category.key]}
                  listingCounts={listingCounts}
                  onViewAll={() => setActiveCategory(category.key)}
                />
              ))}
            </div>
          )}

          {/* Single category carousel - with or without search */}
          {!loading && activeCategory !== 'all' && (
            <div className={css.categoriesContainer}>
              <div className={css.backToAllHeader}>
                <button
                  className={css.backToAll}
                  onClick={() => setActiveCategory('all')}
                >
                  ← Back to all categories
                </button>
              </div>
              <CategoryCarouselSection
                key={activeCategory}
                category={SELLER_CATEGORIES.find(c => c.key === activeCategory)}
                sellers={filteredSellers}
                listingCounts={listingCounts}
                onViewAll={() => {}}
              />
            </div>
          )}

          {/* No Results */}
          {!loading && filteredSellers.length === 0 && (
            <div className={css.noResults}>
              <p>No sellers found matching your criteria.</p>
              <button
                className={css.resetBtn}
                onClick={() => {
                  setSearchQuery('');
                  setActiveCategory('all');
                }}
              >
                Reset filters
              </button>
            </div>
          )}
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const AllSellersPage = compose(
  connect(mapStateToProps)
)(AllSellersPageComponent);

export default AllSellersPage;
